import { Fragment } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BenzoComparisonBlock } from "@/components/BenzoComparisonBlock";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { formatDate } from "@/utils/formatDate";
import { PageHeader } from "@/components/PageHeader";
import { NeonCard } from "@/components/NeonCard";
import { getPublishedPostBySlug } from "@/lib/public-content.functions";
import { sanitizeArticleHtml } from "@/lib/sanitizeArticleHtml";
import { buildMeta } from "@/lib/seo";
import { PageSources } from "@/components/SourcesSheet";
import { errorMessage } from "@/lib/error-capture";

export const Route = createFileRoute("/harm-reduction_/$slug")({
  loader: ({ params }) =>
    getPublishedPostBySlug({ data: { type: "harmreduction", slug: params.slug } }),
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return buildMeta({
        title: "Artikel nicht gefunden - Harm Reduction",
        description: "Der gesuchte Harm-Reduction-Artikel existiert nicht (mehr).",
        path: `/harm-reduction/${params.slug}`,
        noindex: true,
      });
    }
    const base = buildMeta({
      title: `${loaderData.title} - Harm Reduction`,
      description: loaderData.excerpt,
      path: `/harm-reduction/${params.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.title,
            description: loaderData.excerpt,
            datePublished: loaderData.published_date,
            inLanguage: "de",
            mainEntityOfPage: `https://restlicht.fyi/harm-reduction/${params.slug}`,
            author: { "@type": "Organization", name: "Restlicht.FYI" },
            publisher: { "@type": "Organization", name: "Restlicht.FYI" },
          }),
        },
      ],
    };
  },

  errorComponent: ({ error }) => (
    <div role="alert" style={{ padding: "2rem", color: "var(--text-secondary)" }}>
      Fehler: {errorMessage(error)}
    </div>
  ),
  notFoundComponent: () => (
    <div style={{ padding: "2rem" }}>Artikel nicht gefunden.</div>
  ),
  component: HarmReductionDetail,
});

function HarmReductionDetail() {
  const post = Route.useLoaderData();

  if (!post) {
    return (
      <div data-section="hr">
        <PageHeader icon={<ShieldAlert size={28} />} title="Artikel nicht gefunden" />
        <NeonCard style={{ padding: "1.5rem" }}>
          <p style={{ color: "var(--text-secondary)", marginBottom: "1rem" }}>
            Der gesuchte Harm-Reduction-Artikel existiert nicht (mehr).
          </p>
          <Link
            to="/harm-reduction"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              color: "var(--accent)",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            <ArrowLeft size={16} /> Zurück zu Harm Reduction
          </Link>
        </NeonCard>
      </div>
    );
  }

  return (
    <div data-section="hr">
      <PageHeader icon={<ShieldAlert size={28} />} title={post.title} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          {formatDate(post.published_date)}
        </span>
        <span
          style={{
            background: "var(--accent-bg)",
            color: "var(--accent)",
            border: "1px solid var(--accent-border)",
            borderRadius: "20px",
            padding: "0.2rem 0.6rem",
            fontSize: "0.8rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Harm Reduction
        </span>
      </div>

      <NeonCard as="article" style={{ padding: "1.75rem" }}>
        <ArticleBody html={post.content} />
      </NeonCard>


      <div style={{ marginTop: "1.5rem" }}>
        <Link
          to="/harm-reduction"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            color: "var(--accent)",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          <ArrowLeft size={16} /> Zurück zu Harm Reduction
        </Link>
      </div>
      <PageSources path="/harm-reduction" />
    </div>
  );
}

const BLOCK_PATTERN = /\[\[benzo-vergleich(?::klassen)?\]\]/g;

/** Renders sanitized article HTML and replaces benzo placeholders with the shared block. */
function ArticleBody({ html }: { html: string | null | undefined }) {
  const raw = html ?? "";
  const parts = raw.split(BLOCK_PATTERN);
  const tokens = raw.match(BLOCK_PATTERN) ?? [];

  return (
    <>
      {parts.map((chunk, i) => (
        <Fragment key={i}>
          {chunk.trim() !== "" && (
            <div
              className="hr-article-content"
              dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(chunk) }}
            />
          )}
          {tokens[i] && (
            <BenzoComparisonBlock variant={tokens[i].includes(":klassen") ? "classes" : "table"} />
          )}
        </Fragment>
      ))}
    </>
  );
}
